import { useState } from 'react';
import { Check, Car, Building2, FileText, Users } from 'lucide-react';
import { useB2BAccess, getModuleInfo } from '@/hooks/useB2BAccess';
import { type B2BModule } from '@/config/plans';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const MODULE_ICONS: Record<B2BModule, typeof Car> = {
  fleet: Car,
  properties: Building2,
  tenders: FileText,
  subcontractors: Users,
};

interface B2BModuleSelectorProps {
  targetModule?: B2BModule;
  onSelected?: () => void;
}

/**
 * Component for Pro users to select their B2B module
 */
export function B2BModuleSelector({ targetModule, onSelected }: B2BModuleSelectorProps) {
  const { selectModule, isLoading } = useB2BAccess();
  const [selectedModule, setSelectedModule] = useState<B2BModule | null>(targetModule || null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const modules: B2BModule[] = ['fleet', 'properties', 'tenders', 'subcontractors'];

  const handleSelect = async () => {
    if (!selectedModule) {
      toast.error('Veuillez sélectionner un module');
      return;
    }

    setIsSubmitting(true);
    try {
      await selectModule(selectedModule);
      toast.success(`Module ${getModuleInfo(selectedModule).name} activé !`);
      onSelected?.();
    } catch (error) {
      console.error('Error selecting module:', error);
      toast.error('Erreur lors de la sélection du module');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Choisissez votre module B2B</CardTitle>
          <CardDescription className="text-base mt-2">
            Votre plan Pro inclut un module métier. Sélectionnez celui qui correspond le mieux à votre activité.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {modules.map((module) => {
              const info = getModuleInfo(module);
              const Icon = MODULE_ICONS[module];
              const isSelected = selectedModule === module;

              return (
                <button
                  key={module}
                  onClick={() => setSelectedModule(module)}
                  className={`
                    relative p-4 rounded-lg border-2 text-left transition-all
                    ${isSelected 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'}
                  `}
                  disabled={isLoading || isSubmitting}
                >
                  {isSelected && (
                    <div className="absolute top-3 right-3">
                      <Check className="h-5 w-5 text-primary" />
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary/10' : 'bg-muted'}`}>
                      <Icon className={`h-5 w-5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium">{info.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {info.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex flex-col items-center gap-3 pt-4">
            <Button 
              onClick={handleSelect} 
              disabled={!selectedModule || isLoading || isSubmitting}
              size="lg"
              className="w-full max-w-xs"
            >
              {isSubmitting ? 'Activation...' : 'Activer ce module'}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Vous pourrez ajouter d'autres modules à 12€/mois chacun
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
